import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'shared-vectors';
import IconCaret from 'shared-vectors/icons/Caret';
import isFunction from 'shared-utils/src/checkVarType/isFunction';
import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import GridItem from '../GridItem';
import CheckboxField from '../CheckboxField';
import Accordion from '../AccordionItemUnstyled';
import styles, {
    mainCls,
    gridLabelCls,
    gridForDescCls,
    gridWithDescCls,
    gridIconCls,
    labelAccordCls,
    labelNoAccordCls,
} from './styles';

const useStyles = createStyle(styles);

export default function ListCheckboxItem({
    className,
    checked,
    indeterminate,
    onChange,
    label,
    accordion,
    children,
    open,
    onAccordion,
    forDescription,
    withDescription,
    checkedColor,
    checkboxGutters,
}) {
    const classes = useStyles();
    const [chked, setChecked] = useState(checked);
    const [indet, setIndet] = useState(indeterminate);
    useEffect(() => { setChecked(checked) }, [checked]);
    useEffect(() => { setIndet(indeterminate) }, [indeterminate]);
    const cls = makeClassName([ mainCls, className ]);
    const clsGridLabel = makeClassName([
        classes[gridLabelCls],
        accordion ? classes[labelAccordCls] : `${labelNoAccordCls}`,
        forDescription && classes[gridForDescCls],
        withDescription && classes[gridWithDescCls],
    ]);
    const clsGridIcon = makeClassName([classes[gridIconCls]]);
    const handleChange = (chk, opts) => {
        setChecked(chk);
        onAccordion && onAccordion(chk, opts);
        onChange(chk, opts);
    };

    return (
        <Accordion
            className={cls}
            startOpen={open}
            onToggle={onAccordion}
            header={() => <>
                <GridItem onClick={e => e.stopPropagation()} className={clsGridLabel}>
                    <CheckboxField
                        checked={chked}
                        indeterminate={indet}
                        onChange={handleChange}
                        color={checkedColor || null}
                        gutters={checkboxGutters}
                    >
                        { isFunction(label) ? label({ checked: chked }) : label }
                    </CheckboxField>
                </GridItem>
            </>}
            trigger={(opened, hasRotate) => (
                accordion &&
                <GridItem className={clsGridIcon} behavior="fixed" push="left">
                  <Icon name={IconCaret} className={`is-anim ${hasRotate ? 'rotate--180' : ''}`}/>
                </GridItem>
            )}
        >
            {children}
        </Accordion>
    );
};

ListCheckboxItem.propTypes = {
    /**
     * Component class
     */
    className: PropTypes.string,

    /**
     * Initial checked state
     */
    checked: PropTypes.bool,

    /**
     * Initial indeterminate state
     */
    indeterminate: PropTypes.bool,

    /**
     * Callback when the input is changed
     */
    onChange: PropTypes.func,

    /**
     * Callback when the input is changed
     */
    onAccordion: PropTypes.func,

    /**
     * Checkbox label
     */
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
        PropTypes.func,
    ]),

    /**
     * Enable accordion
     */
    accordion: PropTypes.bool,

    /**
     * Render with open accordion
     */
    open: PropTypes.bool,

    hasDescription: PropTypes.bool,

    checkedColor: PropTypes.string,
    checkboxGutters: PropTypes.string,
};

ListCheckboxItem.defaultProps = {
    className: '',
    checked: false,
    indeterminate: false,
    label: '',
    accordion: true,
    open: true,
    onChange: () => {},
    onAccordion: () => {},
    hasDescription: false,
    checkedColor: '',
    checkboxGutters: 'l',
};